import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { SendMessageInput } from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useState } from "react";
import { catchApiErrors } from "../db/error-handling";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";

interface UseSendMessageOptions {
  conversationId: ConversationId;
  channel?: MessageChannel;
}

/**
 * Hook to send a message.
 * Optimistically marks the draft as SENDING in the collection so the
 * UI immediately reflects the in-flight state. The real message arrives via
 * the SSE messageCreated event, which triggers a collection invalidation.
 */
export function useSendMessage({
  conversationId,
  channel: _channel,
}: UseSendMessageOptions) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const collections = useGlobalConversationCollections();
  const [isSending, setIsSending] = useState(false);

  const sendMessage = async (input: SendMessageInput) => {
    setIsSending(true);
    try {
      // Optimistically mark the draft as SENDING in the collection
      if (input.draftId) {
        collections.drafts.utils.writeUpdate({
          id: input.draftId,
          status: "SENDING" as const,
        });
      }

      const client = effectApi.getClient();
      await effectApi.runPromise(
        catchApiErrors(
          client.conversation.sendMessage({
            path: { id: conversationId },
            payload: input as Extract<SendMessageInput, { kind: "reply" }>,
          }),
        ),
      );
    } finally {
      setIsSending(false);
    }
  };

  return { sendMessage, isSending };
}
